import React, { useState, useRef, useEffect } from 'react';
import './DebugVideoCard.css';
import { buildApiUrl } from '../config/api';

interface DebugVideoCardProps {
  debugVideoUrl?: string | null;
  title?: string;
  subtitle?: string;
  isSaved?: boolean;
  playerId?: number;
  analysisId?: number;
}

const DebugVideoCard: React.FC<DebugVideoCardProps> = ({
  debugVideoUrl,
  title = "Debug Video (Overlay)",
  subtitle = "Shows pose skeleton + keyframes",
  isSaved = false,
  playerId,
  analysisId
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoFps, setVideoFps] = useState<number | null>(null);
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0.5, y: 0.5 }); // Center by default
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [availableVideos, setAvailableVideos] = useState<Array<{id: number, name: string, url: string}>>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [selectedComparisonVideo, setSelectedComparisonVideo] = useState<{url: string, name: string, isFile: boolean} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to advance video by one frame
  const advanceFrame = () => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    // Pause the video first (important when in zoom mode)
    video.pause();
    
    // Get frame rate - try to get from video metadata, or use default 30fps
    const fps = videoFps || 30;
    const frameDuration = 1 / fps;
    
    // Advance by one frame
    const newTime = Math.min(video.currentTime + frameDuration, video.duration);
    video.currentTime = newTime;
  };

  // Function to reverse video by one frame
  const reverseFrame = () => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    // Pause the video first (important when in zoom mode)
    video.pause();
    
    // Get frame rate - try to get from video metadata, or use default 30fps
    const fps = videoFps || 30;
    const frameDuration = 1 / fps;
    
    // Reverse by one frame
    const newTime = Math.max(video.currentTime - frameDuration, 0);
    video.currentTime = newTime;
  };

  // Handle video click for zoom
  const handleVideoZoomClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!isZoomMode || !videoRef.current) return;
    
    const video = videoRef.current;
    const rect = video.getBoundingClientRect();
    
    // Calculate click position relative to video (0-1 range)
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Set zoom origin to clicked position
    setZoomOrigin({ x, y });
    
    // Increase zoom level (3x zoom)
    setZoomLevel(3);
    
    // Pause the video and keep it paused
    video.pause();
    
    // Exit zoom mode after clicking (but stay zoomed)
    setIsZoomMode(false);
  };

  // Reset zoom
  const resetZoom = () => {
    setZoomLevel(1);
    setZoomOrigin({ x: 0.5, y: 0.5 });
    setIsZoomMode(false);
  };

  // Load available videos for comparison
  const loadAvailableVideos = async () => {
    if (!playerId) {
      setAvailableVideos([]);
      return;
    }

    setLoadingVideos(true);
    try {
      const token = localStorage.getItem("access_token") || localStorage.getItem("futeval_access_token") || "";
      const response = await fetch(buildApiUrl(`/video-kinematics/player/${playerId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const videos = (data.analyses || [])
          .filter((a: any) => a.id !== analysisId && (a.debugVideoUrl || a.video?.debugUrl || a.debug_video_url))
          .map((a: any) => ({
            id: a.id,
            name: `${a.movement_name || 'Analysis'} - ${new Date(a.timestamp).toLocaleDateString()}`,
            url: a.debugVideoUrl || a.video?.debugUrl || a.debug_video_url
          }));
        setAvailableVideos(videos);
      }
    } catch (error) {
      console.error('Failed to load available videos:', error);
      setAvailableVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  // Open comparison modal
  const handleOpenComparison = () => {
    // Reset popup blocked state
    setPopupBlocked(false);
    
    // Just open the modal - window will open when user selects a video
    setShowComparisonModal(true);
    loadAvailableVideos();
  };

  // Handle file selection for comparison
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Always use blob URL - we'll fetch it properly in the popup window
      // Data URLs can have codec issues with video files
      const fileUrl = URL.createObjectURL(file);
      setSelectedComparisonVideo({
        url: fileUrl,
        name: 'Uploaded Video',
        isFile: true
      });
    }
  };
  
  // Handle starting the comparison - called when user clicks "Compare" button
  const handleStartComparison = () => {
    if (!selectedComparisonVideo) {
      alert('Please select a video to compare first.');
      return;
    }
    
    // Close modal
    setShowComparisonModal(false);
    
    // Open comparison window - this is a direct button click, so popup blocker should allow it
    openSideBySideComparison(
      selectedComparisonVideo.url,
      selectedComparisonVideo.name,
      null
    );
    
    // Clear selection
    setSelectedComparisonVideo(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open side-by-side comparison
  const openSideBySideComparison = (secondVideoUrl: string, secondVideoName: string = 'Comparison Video', preOpenedWindow: Window | null = null) => {
    // Check if we have at least one video source
    if (!videoObjectUrl && !debugVideoUrl) {
      console.error('No video source available for comparison');
      alert('No video available for comparison. Please ensure the debug video has loaded.');
      return;
    }
    
    console.log('Opening side-by-side comparison:', {
      videoObjectUrl,
      debugVideoUrl,
      secondVideoUrl,
      secondVideoName
    });

    const token = localStorage.getItem("access_token") || localStorage.getItem("futeval_access_token") || "";
    
    let resolvedUrl1: string = '';
    let video1IsBlob: boolean = false;
    // Priority: debugVideoUrl (API URL) > videoObjectUrl (blob URL)
    if (debugVideoUrl) {
      if (debugVideoUrl.startsWith("http://") || debugVideoUrl.startsWith("https://")) {
        resolvedUrl1 = debugVideoUrl;
      } else {
        const cleanPath = debugVideoUrl.startsWith("/api/") 
          ? debugVideoUrl.slice(4)
          : debugVideoUrl.startsWith("/")
          ? debugVideoUrl.slice(1)
          : debugVideoUrl;
        resolvedUrl1 = buildApiUrl(cleanPath);
      }
    } else if (videoObjectUrl) {
      // If we only have a blob URL, pass it to the popup to fetch
      resolvedUrl1 = videoObjectUrl;
      video1IsBlob = true;
    }
    
    console.log('Resolved URLs for side-by-side:', {
      debugVideoUrl,
      videoObjectUrl,
      resolvedUrl1,
      secondVideoUrl
    });

    // Resolve second video URL (if it's a blob URL, use it directly; otherwise treat as API URL)
    let resolvedUrl2 = secondVideoUrl;
    if (!secondVideoUrl.startsWith('blob:') && !secondVideoUrl.startsWith('http://') && !secondVideoUrl.startsWith('https://')) {
      const cleanPath = secondVideoUrl.startsWith("/api/") 
        ? secondVideoUrl.slice(4)
        : secondVideoUrl.startsWith("/")
        ? secondVideoUrl.slice(1)
        : secondVideoUrl;
      resolvedUrl2 = buildApiUrl(cleanPath);
    }

    // Create HTML content for side-by-side comparison
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <title>Side-by-Side Video Comparison</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background: #000;
      display: flex;
      flex-direction: column;
      height: 100vh;
      width: 100vw;
      overflow: hidden;
      font-family: Arial, sans-serif;
    }
    .comparison-container {
      display: flex;
      flex: 1;
      gap: 10px;
      padding: 10px;
    }
    .video-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #111;
      border-radius: 8px;
      overflow: visible;
      position: relative;
      min-height: 400px;
    }
    .video-label {
      position: absolute;
      top: 10px;
      left: 10px;
      background: rgba(0, 0, 0, 0.7);
      color: #fff;
      padding: 5px 10px;
      border-radius: 4px;
      font-size: 12px;
      z-index: 100;
      pointer-events: none;
    }
    video {
      width: 100%;
      height: 100%;
      min-height: 300px;
      max-height: calc(100vh - 200px);
      object-fit: contain;
      cursor: pointer;
      background: #000;
      flex: 1;
    }
    .video-controls {
      position: absolute;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: center;
      background: rgba(0, 0, 0, 0.7);
      padding: 0.5rem;
      border-radius: 6px;
    }
    .frame-btn {
      background: #567ad8;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 0.5rem 1rem;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: Arial, sans-serif;
    }
    .frame-btn:hover {
      background: #4569c7;
      transform: translateY(-1px);
    }
    .frame-btn:active {
      transform: translateY(0);
    }
    .close-hint {
      position: fixed;
      top: 20px;
      right: 20px;
      color: #fff;
      background: rgba(0, 0, 0, 0.7);
      padding: 10px 15px;
      border-radius: 5px;
      font-size: 14px;
      z-index: 1000;
      pointer-events: none;
      animation: fadeOut 3s forwards;
    }
    @keyframes fadeOut {
      0% { opacity: 1; }
      70% { opacity: 1; }
      100% { opacity: 0; }
    }
  </style>
</head>
<body>
  <div class="close-hint">Press ESC to close</div>
  <div class="comparison-container">
    <div class="video-wrapper" id="videoWrapper1">
      <div class="video-label">Current Video</div>
      <video controls playsinline id="video1"></video>
      <div class="video-controls" id="controls1">
        <button class="frame-btn" id="reverse1">⏪ Reverse</button>
        <button class="frame-btn" id="advance1">⏩ Advance</button>
        <button class="frame-btn" id="zoom1">🔍 Zoom</button>
        <button class="frame-btn" id="resetZoom1" style="display: none; background: #ef4444;">↩ Reset</button>
      </div>
    </div>
    <div class="video-wrapper" id="videoWrapper2">
      <div class="video-label">${secondVideoName}</div>
      <video controls playsinline id="video2"></video>
      <div class="video-controls" id="controls2">
        <button class="frame-btn" id="reverse2">⏪ Reverse</button>
        <button class="frame-btn" id="advance2">⏩ Advance</button>
        <button class="frame-btn" id="zoom2">🔍 Zoom</button>
        <button class="frame-btn" id="resetZoom2" style="display: none; background: #ef4444;">↩ Reset</button>
      </div>
    </div>
  </div>
  <script>
    console.log('=== SIDE-BY-SIDE COMPARISON SCRIPT LOADING ===');
    (function() {
      console.log('=== IIFE STARTED ===');
      
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        console.log('DOM is loading, waiting for DOMContentLoaded...');
        document.addEventListener('DOMContentLoaded', init);
      } else {
        console.log('DOM already ready, calling init immediately');
        init();
      }
      
      function init() {
        console.log('=== INIT FUNCTION CALLED ===');
        console.log('Document ready state:', document.readyState);
        console.log('Document body:', document.body);
        
        const video1 = document.getElementById('video1');
        const video2 = document.getElementById('video2');
        
        console.log('Video elements found:', { video1: !!video1, video2: !!video2 });
        
        if (!video1 || !video2) {
          const errorMsg = 'Video elements not found! video1=' + !!video1 + ', video2=' + !!video2;
          console.error('Video elements not found!', { 
            video1, 
            video2,
            allElements: document.querySelectorAll('video'),
            bodyHTML: document.body ? document.body.innerHTML.substring(0, 500) : 'no body'
          });
          return;
        }
        
        const token = ${JSON.stringify(token)};
        const resolvedUrl1 = ${JSON.stringify(resolvedUrl1)};
        const resolvedUrl2 = ${JSON.stringify(resolvedUrl2)};
        const video1IsBlob = ${JSON.stringify(video1IsBlob)};
        const video1BlobUrl = ${JSON.stringify(video1IsBlob ? resolvedUrl1 : '')};
        // Don't use blob URLs from parent window - they may not be accessible
        // Always use API URLs (resolvedUrl1/resolvedUrl2) or fetch blob URLs in this window
        const videoObjectUrl1 = ''; // Always use resolvedUrl1 (API URL) or fetch blob
        const videoObjectUrl2 = ''; // Always use resolvedUrl2 or fetch the blob
        const secondVideoUrlIsBlob = ${JSON.stringify(secondVideoUrl.startsWith('blob:'))};
        const secondVideoUrlIsDataUrl = ${JSON.stringify(secondVideoUrl.startsWith('data:'))};
        const secondVideoBlobUrl = ${JSON.stringify(secondVideoUrl.startsWith('blob:') ? secondVideoUrl : '')};
        const secondVideoDataUrl = ${JSON.stringify(secondVideoUrl.startsWith('data:') ? secondVideoUrl : '')};
        
        console.log('=== SIDE-BY-SIDE COMPARISON INITIALIZED ===');
        console.log('URLs:', {
          resolvedUrl1,
          resolvedUrl2,
          video1IsBlob,
          video1BlobUrl,
          videoObjectUrl1,
          videoObjectUrl2,
          secondVideoName: ${JSON.stringify(secondVideoName)},
          secondVideoUrl: ${JSON.stringify(secondVideoUrl)},
          secondVideoUrlIsBlob,
          secondVideoBlobUrl,
          video1Found: !!video1,
          video2Found: !!video2,
          tokenPresent: !!token
        });
        
        // Diagnostic: Check if we have valid video sources
        if (!resolvedUrl1 && !video1BlobUrl) {
          console.error('CRITICAL: No video source for video1!', {
            resolvedUrl1,
            video1IsBlob,
            video1BlobUrl
          });
          showError('videoWrapper1', 'No video source available for video 1');
        }
        if (!resolvedUrl2 && !secondVideoBlobUrl && !secondVideoDataUrl) {
          console.error('CRITICAL: No video source for video2!', {
            resolvedUrl2,
            secondVideoUrlIsBlob,
            secondVideoBlobUrl,
            secondVideoUrlIsDataUrl,
            secondVideoDataUrl
          });
          showError('videoWrapper2', 'No video source available for video 2');
        }
        
        // State for each video
        const state1 = { isZoomMode: false, zoomLevel: 1, zoomOrigin: { x: 0.5, y: 0.5 } };
        const state2 = { isZoomMode: false, zoomLevel: 1, zoomOrigin: { x: 0.5, y: 0.5 } };
        
        // Frame control functions for video 1
        function advanceFrame1() {
        if (!video1) return;
        video1.pause();
        const fps = 30;
        const frameDuration = 1 / fps;
        video1.currentTime = Math.min(video1.currentTime + frameDuration, video1.duration);
      }
      
        function reverseFrame1() {
          if (!video1) return;
          video1.pause();
          const fps = 30;
          const frameDuration = 1 / fps;
          video1.currentTime = Math.max(video1.currentTime - frameDuration, 0);
        }
        
        function handleZoomClick1(e) {
        if (!state1.isZoomMode || !video1) return;
        const rect = video1.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        state1.zoomOrigin = { x, y };
        state1.zoomLevel = 3;
        state1.isZoomMode = false;
        video1.pause();
        video1.style.transform = 'scale(3)';
        video1.style.transformOrigin = (x * 100) + '% ' + (y * 100) + '%';
        document.getElementById('zoom1').style.display = 'none';
        document.getElementById('resetZoom1').style.display = 'inline-block';
      }
      
      function resetZoom1() {
        state1.zoomLevel = 1;
        state1.zoomOrigin = { x: 0.5, y: 0.5 };
        state1.isZoomMode = false;
        if (video1) {
          video1.style.transform = 'scale(1)';
          video1.style.transformOrigin = '50% 50%';
        }
        document.getElementById('zoom1').style.display = 'inline-block';
        document.getElementById('resetZoom1').style.display = 'none';
      }
      
      function enableZoomMode1() {
        state1.isZoomMode = true;
        if (video1) video1.style.cursor = 'crosshair';
        const btn = document.getElementById('zoom1');
        if (btn) {
          btn.textContent = '🔍 Click to Zoom';
          btn.style.background = '#f59e0b';
        }
      }
      
      // Frame control functions for video 2
      function advanceFrame2() {
        if (!video2) return;
        video2.pause();
        const fps = 30;
        const frameDuration = 1 / fps;
        video2.currentTime = Math.min(video2.currentTime + frameDuration, video2.duration);
      }
      
      function reverseFrame2() {
        if (!video2) return;
        video2.pause();
        const fps = 30;
        const frameDuration = 1 / fps;
        video2.currentTime = Math.max(video2.currentTime - frameDuration, 0);
      }
      
      function handleZoomClick2(e) {
        if (!state2.isZoomMode || !video2) return;
        const rect = video2.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        state2.zoomOrigin = { x, y };
        state2.zoomLevel = 3;
        state2.isZoomMode = false;
        video2.pause();
        video2.style.transform = 'scale(3)';
        video2.style.transformOrigin = (x * 100) + '% ' + (y * 100) + '%';
        document.getElementById('zoom2').style.display = 'none';
        document.getElementById('resetZoom2').style.display = 'inline-block';
      }
      
      function resetZoom2() {
        state2.zoomLevel = 1;
        state2.zoomOrigin = { x: 0.5, y: 0.5 };
        state2.isZoomMode = false;
        if (video2) {
          video2.style.transform = 'scale(1)';
          video2.style.transformOrigin = '50% 50%';
        }
        document.getElementById('zoom2').style.display = 'inline-block';
        document.getElementById('resetZoom2').style.display = 'none';
      }
      
      function enableZoomMode2() {
        state2.isZoomMode = true;
        if (video2) video2.style.cursor = 'crosshair';
        const btn = document.getElementById('zoom2');
        if (btn) {
          btn.textContent = '🔍 Click to Zoom';
          btn.style.background = '#f59e0b';
        }
      }
      
      // Define showError function (used by loadVideo1 and loadVideo2)
      function showError(wrapperId, message) {
        const wrapper = document.getElementById(wrapperId);
        if (wrapper) {
          // Remove existing error messages
          const existingErrors = wrapper.querySelectorAll('.video-error');
          existingErrors.forEach(el => el.remove());
          
          const errorDiv = document.createElement('div');
          errorDiv.className = 'video-error';
          errorDiv.style.cssText = 'color: #ff6b6b; padding: 1rem; text-align: center; background: rgba(255, 107, 107, 0.1); border: 1px solid #ff6b6b; border-radius: 4px; margin: 1rem;';
          errorDiv.textContent = message || 'Error loading video';
          wrapper.appendChild(errorDiv);
          console.error('Error displayed in', wrapperId + ':', message);
        } else {
          console.error('Could not find wrapper element:', wrapperId, 'to display error:', message);
        }
      }
      
      // Load video 1
      function loadVideo1() {
        console.log('=== LOAD VIDEO 1 CALLED ===');
        console.log('Loading video 1...', { 
          resolvedUrl1, 
          video1IsBlob, 
          video1BlobUrl, 
          video1: !!video1,
          resolvedUrl1Type: typeof resolvedUrl1,
          resolvedUrl1Length: resolvedUrl1 ? resolvedUrl1.length : 0,
          video1BlobUrlType: typeof video1BlobUrl,
          video1BlobUrlLength: video1BlobUrl ? video1BlobUrl.length : 0
        });
        if (!video1) {
          console.error('Video 1 element not found!');
          showError('videoWrapper1', 'Video element not found');
          return;
        }
        
        // If video 1 is a blob URL from parent window, fetch it in this window context
        if (video1IsBlob && video1BlobUrl && video1BlobUrl.trim() !== '') {
          console.log('Video 1 is blob URL, fetching from parent window:', video1BlobUrl);
          console.log('Video 1 is blob URL from parent, fetching:', video1BlobUrl);
          fetch(video1BlobUrl)
            .then(res => {
              console.log('Video 1 blob fetch response:', res.status, res.statusText, 'Content-Type:', res.headers.get('Content-Type'));
              if (!res.ok) {
                throw new Error('Failed to fetch video 1 blob: ' + res.status + ' ' + res.statusText);
              }
              // Get the content type from response or default to video/mp4
              const contentType = res.headers.get('Content-Type') || 'video/mp4';
              // Use arrayBuffer to preserve binary data integrity
              return res.arrayBuffer().then(buffer => {
                console.log('Video 1 arrayBuffer received, size:', buffer.byteLength, 'bytes, creating blob with type:', contentType);
                // Create blob from arrayBuffer with explicit MIME type
                return new Blob([buffer], { type: contentType });
              });
            })
            .then(blob => {
              console.log('Video 1 blob received, size:', blob.size, 'type:', blob.type);
              if (blob.size === 0) {
                throw new Error('Video 1 blob is empty');
              }
              
              // Create object URL with explicit type
              const blobUrl = URL.createObjectURL(blob);
              console.log('Video 1 blob URL created in new window:', blobUrl);
              
              // Set video source
              video1.src = blobUrl;
              video1.load();
              
              video1.addEventListener('error', function(e) {
                console.error('Video 1 load error:', e, 'Error code:', video1.error?.code, 'Message:', video1.error?.message);
                showError('videoWrapper1', 'Failed to load video: ' + (video1.error?.message || 'Unknown error (code: ' + video1.error?.code + ')'));
              });
              
              video1.addEventListener('loadedmetadata', function() {
                console.log('Video 1 metadata loaded, duration:', video1.duration, 'dimensions:', video1.videoWidth + 'x' + video1.videoHeight);
              });
              
              video1.addEventListener('loadeddata', function() {
                console.log('Video 1 loaded successfully');
              });
              
              video1.addEventListener('canplay', function() {
                console.log('Video 1 can play');
              });
            })
            .catch(err => {
              console.error('Error loading video 1 blob:', err);
              showError('videoWrapper1', 'Error loading video: ' + err.message);
            });
        }
        // If video 1 is an API URL, fetch it with authentication
        else if (resolvedUrl1 && resolvedUrl1.trim() !== '') {
          console.log('Video 1 is API URL, fetching with authentication:', resolvedUrl1);
          console.log('Using token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
          fetch(resolvedUrl1, {
            headers: { 'Authorization': 'Bearer ' + token }
          })
          .then(res => {
            console.log('Video 1 fetch response:', res.status, res.statusText, 'Content-Type:', res.headers.get('Content-Type'));
            if (!res.ok) {
              throw new Error('Failed to fetch video 1: ' + res.status + ' ' + res.statusText);
            }
            return res.blob();
          })
          .then(blob => {
            console.log('Video 1 blob received, size:', blob.size, 'type:', blob.type);
            if (blob.size === 0) {
              throw new Error('Video 1 blob is empty');
            }
            const blobUrl = URL.createObjectURL(blob);
            console.log('Video 1 blob URL created:', blobUrl);
            video1.src = blobUrl;
            video1.load();
            
            video1.addEventListener('error', function(e) {
              console.error('Video 1 load error:', e, 'Error code:', video1.error?.code, 'Message:', video1.error?.message);
              showError('videoWrapper1', 'Failed to load video: ' + (video1.error?.message || 'Unknown error (code: ' + video1.error?.code + ')'));
            });
            
            video1.addEventListener('loadedmetadata', function() {
              console.log('Video 1 metadata loaded, duration:', video1.duration, 'dimensions:', video1.videoWidth + 'x' + video1.videoHeight);
            });
            
            video1.addEventListener('loadeddata', function() {
              console.log('Video 1 loaded successfully');
            });
            
            video1.addEventListener('canplay', function() {
              console.log('Video 1 can play');
            });
          })
          .catch(err => {
            console.error('Error loading video 1:', err);
            showError('videoWrapper1', 'Error loading video: ' + err.message);
          });
        } else {
          console.error('No video source for video 1', {
            resolvedUrl1,
            video1IsBlob,
            video1BlobUrl,
            resolvedUrl1Empty: !resolvedUrl1 || resolvedUrl1.trim() === '',
            video1BlobUrlEmpty: !video1BlobUrl || video1BlobUrl.trim() === ''
          });
          showError('videoWrapper1', 'No video source available. Please ensure the debug video has loaded.');
        }
      }
      
      // Load video 2
      function loadVideo2() {
        console.info('=== LOAD VIDEO 2 CALLED ===');
        console.log('=== LOAD VIDEO 2 CALLED ===');
        console.log('Loading video 2...', { secondVideoUrlIsBlob, secondVideoUrlIsDataUrl, secondVideoBlobUrl, secondVideoDataUrl, resolvedUrl2, video2: !!video2 });
        if (!video2) {
          console.error('ERROR: Video 2 element not found!');
          console.error('Video 2 element not found!');
          return;
        }
        
        // If second video is a data URL from file upload, convert to blob URL with proper MIME type
        if (secondVideoUrlIsDataUrl && secondVideoDataUrl) {
          console.info('Video 2 is data URL, converting to blob with proper MIME type...');
          console.log('Video 2 is a data URL, converting to blob URL');
          
          // Convert data URL to ArrayBuffer first, then create blob with explicit MIME type
          try {
            const base64Data = secondVideoDataUrl.split(',')[1];
            const mimeMatch = secondVideoDataUrl.match(/data:([^;]+)/);
            const mimeType = mimeMatch ? mimeMatch[1] : 'video/mp4';
            
            console.info('Extracting data from data URL, MIME type: ' + mimeType + ', data length: ' + base64Data.length);
            
            // Convert base64 to binary
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            // Create blob with explicit MIME type
            const blob = new Blob([bytes], { type: mimeType });
            console.log('Video 2 blob created from data URL: size=' + blob.size + ', type=' + blob.type);
            console.log('Video 2 blob created from data URL:', blob.size, blob.type);
            
            const blobUrl = URL.createObjectURL(blob);
            console.log('Video 2 blob URL created: ' + blobUrl.substring(0, 50) + '...');
            console.log('Video 2 blob URL created:', blobUrl);
            
            video2.src = blobUrl;
            video2.load();
            
            video2.addEventListener('error', function(e) {
              const errorMsg = 'Video 2 load error: code=' + video2.error?.code + ', message=' + video2.error?.message;
              console.error(errorMsg);
              console.error('Video 2 load error:', e, 'Error code:', video2.error?.code, 'Message:', video2.error?.message);
              console.error('Video 2 src:', video2.src);
              console.error('Video 2 blob type:', blob.type);
              console.error('Video 2 blob size:', blob.size);
              showError2('videoWrapper2', 'Failed to load video: ' + (video2.error?.message || 'Unknown error (code: ' + video2.error?.code + ')'));
            });
            
            video2.addEventListener('loadedmetadata', function() {
              console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
              console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
            });
            
            video2.addEventListener('loadeddata', function() {
              console.log('Video 2 loaded successfully!');
              console.log('Video 2 loaded successfully');
            });
            
            video2.addEventListener('canplay', function() {
              console.log('Video 2 can play');
              console.log('Video 2 can play');
            });
          } catch (err) {
            console.error('ERROR converting data URL to blob: ' + err.message);
            console.error('Error converting data URL to blob:', err);
            showError2('videoWrapper2', 'Error processing video: ' + err.message);
          }
        }
        // If second video is a blob URL from file upload, fetch it in this window context
        else if (secondVideoUrlIsBlob && secondVideoBlobUrl) {
          console.log('Video 2 is blob URL from parent, fetching:', secondVideoBlobUrl);
          console.log('Video 2 is a blob URL from parent window, fetching it:', secondVideoBlobUrl);
          // Fetch the blob from the parent window's blob URL and create a new blob URL in this window
          fetch(secondVideoBlobUrl)
            .then(res => {
              console.log('Video 2 blob fetch response: ' + res.status + ' ' + res.statusText);
              console.log('Video 2 blob fetch response:', res.status, res.statusText, 'Content-Type:', res.headers.get('Content-Type'));
              if (!res.ok) {
                throw new Error('Failed to fetch video 2 blob: ' + res.status + ' ' + res.statusText);
              }
              // Get the content type from response or default to video/mp4
              const contentType = res.headers.get('Content-Type') || 'video/mp4';
              // Use arrayBuffer to preserve binary data integrity
              return res.arrayBuffer().then(buffer => {
                console.info('Video 2 arrayBuffer received, size: ' + buffer.byteLength + ' bytes, creating blob with type: ' + contentType);
                // Create blob from arrayBuffer with explicit MIME type
                return new Blob([buffer], { type: contentType });
              });
            })
            .then(blob => {
              console.log('Video 2 blob received, size: ' + blob.size + ' bytes, type: ' + blob.type);
              console.log('Video 2 blob received, size:', blob.size, 'type:', blob.type);
              if (blob.size === 0) {
                throw new Error('Video 2 blob is empty');
              }
              
              // Create object URL with explicit type
              const blobUrl = URL.createObjectURL(blob);
              console.log('Video 2 blob URL created:', blobUrl);
              console.log('Video 2 blob URL created in new window:', blobUrl);
              
              // Set video source
              video2.src = blobUrl;
              video2.load();
              
              // Add error handler before setting src
              video2.addEventListener('error', function(e) {
                const errorMsg = 'Video 2 load error: code=' + video2.error?.code + ', message=' + video2.error?.message;
                console.error(errorMsg);
                console.error('Video 2 load error:', e, 'Error code:', video2.error?.code, 'Message:', video2.error?.message);
                console.error('Video 2 src:', video2.src);
                console.error('Video 2 currentSrc:', video2.currentSrc);
                console.error('Video 2 networkState:', video2.networkState);
                console.error('Video 2 readyState:', video2.readyState);
                
                // Try alternative: create a data URL as fallback (for small files)
                if (blob.size < 50 * 1024 * 1024) { // Only for files < 50MB
                  console.info('Attempting fallback: converting blob to data URL...');
                  const reader = new FileReader();
                  reader.onload = function() {
                    try {
                      video2.src = reader.result;
                      video2.load();
                      console.info('Using data URL fallback for video 2');
                    } catch (err) {
                      console.error('Data URL fallback also failed: ' + err.message);
                      showError2('videoWrapper2', 'Failed to load video: ' + (video2.error?.message || 'Unknown error (code: ' + video2.error?.code + ')'));
                    }
                  };
                  reader.onerror = function() {
                    showError2('videoWrapper2', 'Failed to load video: ' + (video2.error?.message || 'Unknown error (code: ' + video2.error?.code + ')'));
                  };
                  reader.readAsDataURL(blob);
                } else {
                  showError2('videoWrapper2', 'Failed to load video: ' + (video2.error?.message || 'Unknown error (code: ' + video2.error?.code + '). File may be too large or in unsupported format.'));
                }
              });
              
              video2.addEventListener('loadedmetadata', function() {
                console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
                console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
              });
              
              video2.addEventListener('loadeddata', function() {
                console.log('Video 2 loaded successfully!');
                console.log('Video 2 loaded successfully');
              });
              
              video2.addEventListener('canplay', function() {
                console.log('Video 2 can play');
                console.log('Video 2 can play');
              });
            })
            .catch(err => {
              console.error('ERROR loading video 2 blob: ' + err.message);
              console.error('Error loading video 2 blob:', err);
              showError2('videoWrapper2', 'Error loading video: ' + err.message);
            });
        } else if (resolvedUrl2 && resolvedUrl2.trim() !== '') {
          console.info('Fetching video 2 from API: ' + resolvedUrl2);
          console.log('Fetching video 2 from API:', resolvedUrl2);
          fetch(resolvedUrl2, {
            headers: { 'Authorization': 'Bearer ' + token }
          })
          .then(res => {
            console.log('Video 2 fetch response: ' + res.status + ' ' + res.statusText);
            console.log('Video 2 fetch response:', res.status, res.statusText, 'Content-Type:', res.headers.get('Content-Type'));
            if (!res.ok) {
              throw new Error('Failed to fetch video 2: ' + res.status + ' ' + res.statusText);
            }
            return res.blob();
          })
          .then(blob => {
            console.log('Video 2 blob received, size: ' + blob.size + ' bytes, type: ' + blob.type);
            console.log('Video 2 blob received, size:', blob.size, 'type:', blob.type);
            if (blob.size === 0) {
              throw new Error('Video 2 blob is empty');
            }
            const blobUrl = URL.createObjectURL(blob);
            console.log('Video 2 blob URL created:', blobUrl);
            video2.src = blobUrl;
            video2.load();
            
            video2.addEventListener('error', function(e) {
              const errorMsg = 'Video 2 load error: code=' + video2.error?.code + ', message=' + video2.error?.message;
              console.error(errorMsg);
              console.error('Video 2 load error:', e, 'Error code:', video2.error?.code, 'Message:', video2.error?.message);
              showError2('videoWrapper2', 'Failed to load video: ' + (video2.error?.message || 'Unknown error (code: ' + video2.error?.code + ')'));
            });
            
            video2.addEventListener('loadedmetadata', function() {
              console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
              console.log('Video 2 metadata loaded, duration:', video2.duration, 'dimensions:', video2.videoWidth + 'x' + video2.videoHeight);
            });
            
            video2.addEventListener('loadeddata', function() {
              console.log('Video 2 loaded successfully!');
              console.log('Video 2 loaded successfully');
            });
            
            video2.addEventListener('canplay', function() {
              console.log('Video 2 can play');
              console.log('Video 2 can play');
            });
          })
          .catch(err => {
            console.error('ERROR loading video 2: ' + err.message);
            console.error('Error loading video 2:', err);
            showError2('videoWrapper2', 'Error loading video: ' + err.message);
          });
        } else {
          console.error('ERROR: No video source for video 2');
          console.error('No video source for video 2');
          showError2('videoWrapper2');
        }
        
        function showError2(wrapperId, message) {
          const wrapper = document.getElementById(wrapperId);
          if (wrapper) {
            // Remove existing error messages
            const existingErrors = wrapper.querySelectorAll('.video-error');
            existingErrors.forEach(el => el.remove());
            
            const errorDiv = document.createElement('div');
            errorDiv.className = 'video-error';
            errorDiv.style.cssText = 'color: #ff6b6b; padding: 1rem; text-align: center; background: rgba(255, 107, 107, 0.1); border: 1px solid #ff6b6b; border-radius: 4px; margin: 1rem;';
            errorDiv.textContent = message;
            wrapper.appendChild(errorDiv);
          }
        }
      }
      
        // Attach event listeners for video 1
        const advance1 = document.getElementById('advance1');
        const reverse1 = document.getElementById('reverse1');
        const zoom1 = document.getElementById('zoom1');
        const resetZoom1Btn = document.getElementById('resetZoom1');
        
        if (advance1) advance1.addEventListener('click', advanceFrame1);
        if (reverse1) reverse1.addEventListener('click', reverseFrame1);
        if (zoom1) {
          zoom1.addEventListener('click', function() {
            if (state1.zoomLevel > 1) resetZoom1(); else enableZoomMode1();
          });
        }
        if (resetZoom1Btn) resetZoom1Btn.addEventListener('click', resetZoom1);
        if (video1) video1.addEventListener('click', handleZoomClick1);
        
        // Attach event listeners for video 2
        const advance2 = document.getElementById('advance2');
        const reverse2 = document.getElementById('reverse2');
        const zoom2 = document.getElementById('zoom2');
        const resetZoom2Btn = document.getElementById('resetZoom2');
        
        if (advance2) advance2.addEventListener('click', advanceFrame2);
        if (reverse2) reverse2.addEventListener('click', reverseFrame2);
        if (zoom2) {
          zoom2.addEventListener('click', function() {
            if (state2.zoomLevel > 1) resetZoom2(); else enableZoomMode2();
          });
        }
        if (resetZoom2Btn) resetZoom2Btn.addEventListener('click', resetZoom2);
        if (video2) video2.addEventListener('click', handleZoomClick2);
        
        console.log('Event listeners attached:', {
          advance1: !!advance1,
          reverse1: !!reverse1,
          zoom1: !!zoom1,
          advance2: !!advance2,
          reverse2: !!reverse2,
          zoom2: !!zoom2
        });
        
        // Load videos
        console.info('=== CALLING loadVideo1() and loadVideo2() ===');
        console.log('=== CALLING loadVideo1() and loadVideo2() ===');
        try {
          loadVideo1();
        } catch (e) {
          console.error('ERROR calling loadVideo1: ' + e.message);
          console.error('Error calling loadVideo1:', e);
        }
        try {
          loadVideo2();
        } catch (e) {
          console.error('ERROR calling loadVideo2: ' + e.message);
          console.error('Error calling loadVideo2:', e);
        }
        
        // Handle ESC key
        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape' || e.key === 'Esc') {
            window.close();
          }
        });
        
        // Cleanup on close
        window.addEventListener('beforeunload', function() {
          if (video1 && video1.src && video1.src.startsWith('blob:')) URL.revokeObjectURL(video1.src);
          if (video2 && video2.src && video2.src.startsWith('blob:')) URL.revokeObjectURL(video2.src);
        });
      }
    })();
  </script>
</body>
</html>`;

    // Create blob URL for the HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const screenWidth = window.screen.width || 1920;
    const screenHeight = window.screen.height || 1080;
    
    // Use pre-opened window if provided, otherwise try to open new one
    let newWindow: Window | null = preOpenedWindow;
    
    if (!newWindow || newWindow.closed) {
      // Try to open new window (fallback if pre-opened window was closed)
      try {
        newWindow = window.open(
          blobUrl,
          '_blank',
          `width=${screenWidth},height=${screenHeight},left=0,top=0,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
        );
      } catch (e) {
        console.error('Error opening window:', e);
      }
    } else {
      // Open new window with blob URL
      // This should work because it's called directly from user interaction (file select or dropdown change)
      try {
        newWindow = window.open(
          blobUrl,
          '_blank',
          `width=${screenWidth},height=${screenHeight},left=0,top=0,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
        );
      } catch (e) {
        console.error('Error opening window:', e);
      }
    }
    
    // Check if popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      URL.revokeObjectURL(blobUrl);
      
      // More helpful error message with instructions
      const userMessage = `Popup blocked! To use side-by-side comparison:\n\n` +
        `1. Check Chrome's popup blocker settings for this site\n` +
        `2. Look for a popup icon in the address bar and click "Always allow popups"\n` +
        `3. Or go to Chrome Settings > Privacy and security > Site settings > Pop-ups and redirects\n` +
        `4. Add this site to the allowed list\n` +
        `5. Try again after allowing popups\n\n` +
        `Note: The popup must open immediately when you select a video. If it's still blocked, try:\n` +
        `- Refreshing the page\n` +
        `- Checking if any browser extensions are blocking popups\n` +
        `- Trying in an incognito window to test`;
      
      alert(userMessage);
      return;
    }
    
    // Verify window opened successfully after a brief delay
    setTimeout(() => {
      if (newWindow && (newWindow.closed || typeof newWindow.closed === 'undefined')) {
        URL.revokeObjectURL(blobUrl);
        alert('Popup window was closed or blocked. Please ensure popups are allowed for this site.');
      }
    }, 500);
    
    // Clean up blob URL after window has loaded it
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };

  // Fetch video as blob with authentication and create object URL
  useEffect(() => {
    if (!debugVideoUrl) {
      setVideoObjectUrl(null);
      setIsLoading(false);
      setHasError(false);
      setErrorMessage(null);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setErrorMessage(null);

    // Check for token in both possible keys
    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("futeval_access_token") ||
      "";

    if (!token) {
      setHasError(true);
      setErrorMessage("You are not authenticated. Please log out and log in again.");
      setIsLoading(false);
      return;
    }

    // Resolve URL - if absolute use it, otherwise use buildApiUrl helper
    // Note: debugVideoUrl from backend is typically "/api/video-kinematics/.../debug-video"
    // buildApiUrl will handle it correctly (removes /api if present and adds it back)
    let resolvedUrl: string;
    if (debugVideoUrl.startsWith("http://") || debugVideoUrl.startsWith("https://")) {
      resolvedUrl = debugVideoUrl;
    } else {
      // Remove leading /api if present (buildApiUrl will add it back)
      const cleanPath = debugVideoUrl.startsWith("/api/") 
        ? debugVideoUrl.slice(4) // Remove "/api"
        : debugVideoUrl.startsWith("/")
        ? debugVideoUrl.slice(1) // Remove leading "/"
        : debugVideoUrl;
      resolvedUrl = buildApiUrl(cleanPath);
    }

    // Dev-only: Log the resolved URL to verify it's correct (no /api/api)
    if (process.env.NODE_ENV === 'development') {
      console.log("[DebugVideoCard] Original debugVideoUrl:", debugVideoUrl);
      console.log("[DebugVideoCard] Resolved debug-video URL:", resolvedUrl);
      if (resolvedUrl.includes('/api/api')) {
        console.error("[DebugVideoCard] ERROR: Double /api/api detected in URL!");
      }
      // Assert expected format
      const expectedPattern = /^https?:\/\/[^/]+\/api\/video-kinematics\/[^/]+\/debug-video$/;
      if (!expectedPattern.test(resolvedUrl)) {
        console.warn("[DebugVideoCard] URL format may be incorrect:", resolvedUrl);
      }
    }
    console.log("Debug video url:", resolvedUrl);
    console.log("Debug video auth token present:", !!token, token ? token.slice(0, 10) : "");

    let cancelled = false;
    let currentObjectUrl: string | null = null;

    // Fetch video with authentication
    fetch(resolvedUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        console.log("Debug video fetch status:", res.status);

        if (res.status === 401 || res.status === 403) {
          setHasError(true);
          setErrorMessage("Unauthorized to view debug video. Please log in again.");
          setIsLoading(false);
          return null;
        }

        if (res.status === 404) {
          // Debug video not found - likely ephemeral storage issue in Cloud Run
          // Still show the button so user can try to open it (might work if file exists on different instance)
          setHasError(true);
          setErrorMessage(
            "Debug video is not available. This may happen if the server restarted. " +
            "Please re-analyze the video to generate a new debug video."
          );
          setIsLoading(false);
          // Don't return null - allow button to show even with 404 so user can try
          return null;
        }

        if (!res.ok) {
          throw new Error(`Failed to load video: ${res.status} ${res.statusText}`);
        }

        const blob = await res.blob();
        if (cancelled) {
          return null;
        }

        // Create object URL from blob
        currentObjectUrl = URL.createObjectURL(blob);
        setVideoObjectUrl(currentObjectUrl);
        setIsLoading(false);
        return currentObjectUrl;
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error("Error loading debug video:", error);
        setHasError(true);
        setErrorMessage(error.message || "Could not load debug video.");
        setIsLoading(false);
        setVideoObjectUrl(null);
      });

    // Cleanup: revoke object URL when component unmounts or URL changes
    return () => {
      cancelled = true;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [debugVideoUrl]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (videoObjectUrl) {
        URL.revokeObjectURL(videoObjectUrl);
      }
    };
  }, [videoObjectUrl]);

  const handleVideoClick = () => {
    if (!videoRef.current || !debugVideoUrl) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsPlaying(false);
  };

  const handleEnlarge = () => {
    if (!videoObjectUrl && !debugVideoUrl) return;

    // Get the resolved URL for the video
    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("futeval_access_token") ||
      "";
    
    let resolvedUrl: string;
    if ((debugVideoUrl && debugVideoUrl.startsWith("http://")) || debugVideoUrl?.startsWith("https://")) {
      resolvedUrl = debugVideoUrl;
    } else if (debugVideoUrl) {
      const cleanPath = debugVideoUrl.startsWith("/api/") 
        ? debugVideoUrl.slice(4)
        : debugVideoUrl.startsWith("/")
        ? debugVideoUrl.slice(1)
        : debugVideoUrl;
      resolvedUrl = buildApiUrl(cleanPath);
    } else {
      // Fallback: use videoObjectUrl if available
      resolvedUrl = videoObjectUrl || '';
    }

    // Create HTML content as a string
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <title>Debug Video - Fullscreen</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background: #000;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      width: 100vw;
      overflow: hidden;
    }
    video {
      width: 100vw;
      height: 100vh;
      object-fit: contain;
    }
    .close-hint {
      position: fixed;
      top: 20px;
      right: 20px;
      color: #fff;
      background: rgba(0, 0, 0, 0.7);
      padding: 10px 15px;
      border-radius: 5px;
      font-family: Arial, sans-serif;
      font-size: 14px;
      z-index: 1000;
      pointer-events: none;
      animation: fadeOut 3s forwards;
    }
    .frame-controls {
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: center;
    }
    .frame-btn {
      background: #567ad8;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 0.5rem 1rem;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: Arial, sans-serif;
    }
    .frame-btn:hover {
      background: #4569c7;
      transform: translateY(-1px);
    }
    .frame-btn:active {
      transform: translateY(0);
    }
    @keyframes fadeOut {
      0% { opacity: 1; }
      70% { opacity: 1; }
      100% { opacity: 0; }
    }
  </style>
</head>
<body>
  <div class="close-hint">Press ESC to close | Double-click video for fullscreen</div>
  <video controls autoplay playsinline>
    Your browser does not support the video tag.
  </video>
  <div class="frame-controls">
    <button class="frame-btn" id="reverseFrameBtn" title="Reverse video by one frame">⏪ Reverse Frame</button>
    <button class="frame-btn" id="advanceFrameBtn" title="Advance video by one frame">⏩ Advance Frame</button>
    <button class="frame-btn" id="zoomBtn" title="Click to enable zoom mode, then click on video">🔍 Zoom</button>
    <button class="frame-btn" id="resetZoomBtn" title="Reset zoom" style="display: none; background: #ef4444;">↩ Reset Zoom</button>
  </div>
  <script>
    (function() {
      const video = document.querySelector('video');
      const token = ${JSON.stringify(token)};
      const resolvedUrl = ${JSON.stringify(resolvedUrl)};
      const videoObjectUrl = ${JSON.stringify(videoObjectUrl || '')};
      const advanceFrameBtn = document.getElementById('advanceFrameBtn');
      const reverseFrameBtn = document.getElementById('reverseFrameBtn');
      const zoomBtn = document.getElementById('zoomBtn');
      const resetZoomBtn = document.getElementById('resetZoomBtn');
      
      let isZoomMode = false;
      let zoomLevel = 1;
      let zoomOrigin = { x: 0.5, y: 0.5 };
      
      // Function to advance by one frame
      function advanceFrame() {
        if (!video) return;
        // Always pause when advancing frames (important when in zoom mode)
        video.pause();
        const fps = 30; // Default frame rate (can be adjusted if video metadata provides fps)
        const frameDuration = 1 / fps;
        const newTime = Math.min(video.currentTime + frameDuration, video.duration);
        video.currentTime = newTime;
      }
      
      // Function to reverse by one frame
      function reverseFrame() {
        if (!video) return;
        // Always pause when reversing frames (important when in zoom mode)
        video.pause();
        const fps = 30; // Default frame rate (can be adjusted if video metadata provides fps)
        const frameDuration = 1 / fps;
        const newTime = Math.max(video.currentTime - frameDuration, 0);
        video.currentTime = newTime;
      }
      
      // Function to handle zoom click on video
      function handleVideoZoomClick(e) {
        if (!isZoomMode || !video) return;
        
        const rect = video.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        
        zoomOrigin = { x, y };
        zoomLevel = 3;
        isZoomMode = false;
        
        // Pause the video and keep it paused
        video.pause();
        
        video.style.transform = 'scale(3)';
        video.style.transformOrigin = (x * 100) + '% ' + (y * 100) + '%';
        video.style.cursor = 'pointer';
        
        if (zoomBtn) zoomBtn.style.display = 'none';
        if (resetZoomBtn) resetZoomBtn.style.display = 'inline-block';
      }
      
      // Function to reset zoom
      function resetZoom() {
        zoomLevel = 1;
        zoomOrigin = { x: 0.5, y: 0.5 };
        isZoomMode = false;
        
        if (video) {
          video.style.transform = 'scale(1)';
          video.style.transformOrigin = '50% 50%';
          video.style.cursor = 'pointer';
        }
        
        if (zoomBtn) {
          zoomBtn.style.display = 'inline-block';
          zoomBtn.textContent = '🔍 Zoom';
          zoomBtn.style.background = '#567ad8';
        }
        if (resetZoomBtn) resetZoomBtn.style.display = 'none';
      }
      
      // Function to enable zoom mode
      function enableZoomMode() {
        isZoomMode = true;
        if (video) {
          video.style.cursor = 'crosshair';
        }
        if (zoomBtn) {
          zoomBtn.textContent = '🔍 Click to Zoom';
          zoomBtn.style.background = '#f59e0b';
        }
      }
      
      // Attach frame control buttons
      if (advanceFrameBtn) {
        advanceFrameBtn.addEventListener('click', advanceFrame);
      }
      if (reverseFrameBtn) {
        reverseFrameBtn.addEventListener('click', reverseFrame);
      }
      if (zoomBtn) {
        zoomBtn.addEventListener('click', function() {
          if (zoomLevel > 1) {
            resetZoom();
          } else {
            enableZoomMode();
          }
        });
      }
      if (resetZoomBtn) {
        resetZoomBtn.addEventListener('click', resetZoom);
      }
      
      // Handle video click for zoom
      video.addEventListener('click', function(e) {
        if (isZoomMode) {
          handleVideoZoomClick(e);
        }
      });
      
      // Function to enter fullscreen
      function enterFullscreen() {
        if (video.requestFullscreen) {
          video.requestFullscreen().catch(function(err) { console.log('Fullscreen request failed:', err); });
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
        } else if (video.mozRequestFullScreen) {
          video.mozRequestFullScreen();
        } else if (video.msRequestFullscreen) {
          video.msRequestFullscreen();
        }
      }
      
      // Load video - prefer videoObjectUrl if available, otherwise fetch
      if (videoObjectUrl) {
        video.src = videoObjectUrl;
        video.load();
      } else {
        // Fetch video with authentication
        fetch(resolvedUrl, {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        })
        .then(function(res) {
          if (!res.ok) {
            throw new Error('Failed to load video: ' + res.status);
          }
          return res.blob();
        })
        .then(function(blob) {
          const url = URL.createObjectURL(blob);
          video.src = url;
          video.load();
        })
        .catch(function(err) {
          console.error('Error loading video:', err);
          document.body.innerHTML = '<div style="color: white; text-align: center; padding: 50px; font-family: Arial, sans-serif;"><h2>Error loading video</h2><p>' + err.message + '</p><p style="margin-top: 20px;">Press ESC to close this window.</p></div>';
        });
      }

      // Double-click to enter fullscreen
      video.addEventListener('dblclick', enterFullscreen);
      
      // Try to enter fullscreen automatically when video loads
      video.addEventListener('loadedmetadata', function() {
        // Small delay to ensure video is ready
        setTimeout(function() {
          enterFullscreen();
        }, 100);
      });

      // Handle ESC key to close window
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          // Exit fullscreen first if in fullscreen
          var doc = document;
          if (document.fullscreenElement || doc.webkitFullscreenElement || 
              doc.mozFullScreenElement || doc.msFullscreenElement) {
            if (document.exitFullscreen) {
              document.exitFullscreen();
            } else if (doc.webkitExitFullscreen) {
              doc.webkitExitFullscreen();
            } else if (doc.mozCancelFullScreen) {
              doc.mozCancelFullScreen();
            } else if (doc.msExitFullscreen) {
              doc.msExitFullscreen();
            }
          } else {
            window.close();
          }
        }
      });

      // Cleanup blob URL when window closes
      window.addEventListener('beforeunload', () => {
        if (video && video.src && video.src.startsWith('blob:')) {
          URL.revokeObjectURL(video.src);
        }
      });
    })();
  </script>
</body>
</html>`;

    // Create a blob URL for the HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    // Try to open in fullscreen - use screen dimensions
    const screenWidth = window.screen.width || 1920;
    const screenHeight = window.screen.height || 1080;
    
    // Open the blob URL in a new window with fullscreen-like dimensions
    const newWindow = window.open(
      blobUrl, 
      '_blank', 
      `width=${screenWidth},height=${screenHeight},left=0,top=0,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was blocked
      URL.revokeObjectURL(blobUrl);
      
      // Show a more helpful message with instructions
      const userMessage = `Popup blocked! To view the video in fullscreen:\n\n` +
        `1. Check your browser's popup blocker settings\n` +
        `2. Allow popups for this site\n` +
        `3. Click the Fullscreen button again\n\n` +
        `Alternatively, you can:\n` +
        `- Right-click the video and select "Open video in new tab"\n` +
        `- Then press F11 for fullscreen`;
      
      if (window.confirm(userMessage + '\n\nWould you like to try opening in the same window?')) {
        // Fallback: try to use Fullscreen API on current window
        const videoElement = videoRef.current;
        if (videoElement && videoElement.requestFullscreen) {
          videoElement.requestFullscreen().catch(err => {
            console.error('Fullscreen error:', err);
            alert('Fullscreen not available. Please allow popups for this site.');
          });
        } else {
          alert('Fullscreen API not supported. Please allow popups for this site.');
        }
      }
      return;
    }

    // Try to maximize the window (may not work in all browsers due to security)
    try {
      newWindow.moveTo(0, 0);
      newWindow.resizeTo(screenWidth, screenHeight);
    } catch (e) {
      // Ignore - some browsers don't allow this
      console.log('Could not resize window:', e);
    }

    // Clean up blob URL after a short delay (window should have loaded it)
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };

  if (!debugVideoUrl) {
    return (
      <div className="debug-video-card">
        <div className="debug-video-header">
          <h3>{title}</h3>
          {subtitle && <p className="debug-video-subtitle">{subtitle}</p>}
        </div>
        <div className="debug-video-empty">
          <div className="empty-icon">📹</div>
          <p className="empty-message">
            Analysis video not available for this analysis.
          </p>
          {!isSaved && (
            <p className="empty-hint">
              Save this analysis to store the Analysis Video.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="debug-video-card">
      <div className="debug-video-header">
        <h3>{title}</h3>
        {subtitle && <p className="debug-video-subtitle">{subtitle}</p>}
        {/* Show button if we have a video URL OR if we have a debugVideoUrl (even if video failed to load) */}
        {(videoObjectUrl || debugVideoUrl) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                className="debug-video-enlarge-btn"
                onClick={handleEnlarge}
                title="Open video in fullscreen window (Press ESC to close). If popup is blocked, allow popups for this site."
                aria-label="Enlarge video to fullscreen"
              >
                ⛶ Fullscreen
              </button>
              <button
                className="debug-video-enlarge-btn"
                onClick={handleOpenComparison}
                title="Open side-by-side video comparison"
                aria-label="Side-by-side comparison"
                style={{ background: '#10b981' }}
              >
                ⬌ Side-by-Side
              </button>
            </div>
            <span style={{ fontSize: '0.7em', color: '#666', fontStyle: 'italic' }}>
              (Allow popups if blocked)
            </span>
          </div>
        )}
      </div>
      <div className="debug-video-player-container">
        {isLoading ? (
          <div className="debug-video-loading">
            <div className="loading-spinner">⏳</div>
            <p>Loading debug video...</p>
          </div>
        ) : hasError ? (
          <div className="debug-video-error">
            <div className="error-icon">⚠️</div>
            <p>{errorMessage || "Could not load debug video."}</p>
            {errorMessage && errorMessage.includes("Unauthorized") && (
              <p className="error-details">Please try refreshing the page or logging in again.</p>
            )}
          </div>
        ) : videoObjectUrl ? (
          <video
            ref={videoRef}
            className="debug-video-player"
            controls
            playsInline
            preload="metadata"
            onClick={(e) => {
              if (isZoomMode) {
                handleVideoZoomClick(e);
              } else {
                handleVideoClick();
              }
            }}
            onPlay={handlePlay}
            onPause={handlePause}
            onError={handleError}
            onLoadedMetadata={() => {
              // Try to get frame rate from video metadata if available
              if (videoRef.current) {
                // Note: Most browsers don't expose fps directly, so we'll use a default
                // You could potentially extract this from the video metadata if available
                setVideoFps(30); // Default to 30fps
              }
            }}
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`,
              transition: zoomLevel === 1 ? 'transform 0.3s ease' : 'none',
              cursor: isZoomMode ? 'crosshair' : 'pointer'
            }}
            src={videoObjectUrl}
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="debug-video-error">
            <div className="error-icon">⚠️</div>
            <p>Video URL not available.</p>
          </div>
        )}
      </div>
      {videoObjectUrl && !isLoading && !hasError && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '0.5rem',
          marginTop: '0.5rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid #383b48',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={reverseFrame}
            style={{
              background: '#567ad8',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#4569c7';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#567ad8';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            title="Reverse video by one frame"
          >
            ⏪ Reverse Frame
          </button>
          <button
            onClick={advanceFrame}
            style={{
              background: '#567ad8',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#4569c7';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#567ad8';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            title="Advance video by one frame"
          >
            ⏩ Advance Frame
          </button>
          <button
            onClick={() => {
              if (zoomLevel > 1) {
                resetZoom();
              } else {
                setIsZoomMode(true);
              }
            }}
            style={{
              background: isZoomMode ? '#f59e0b' : zoomLevel > 1 ? '#ef4444' : '#567ad8',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isZoomMode && zoomLevel === 1) {
                e.currentTarget.style.background = '#4569c7';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isZoomMode && zoomLevel === 1) {
                e.currentTarget.style.background = '#567ad8';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            title={isZoomMode ? "Click on video to zoom into that area" : zoomLevel > 1 ? "Reset zoom" : "Click to enable zoom mode, then click on video"}
          >
            {isZoomMode ? '🔍 Click to Zoom' : zoomLevel > 1 ? '↩ Reset Zoom' : '🔍 Zoom'}
          </button>
        </div>
      )}

      {/* Comparison Modal */}
      {showComparisonModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => {
          setShowComparisonModal(false);
        }}>
          <div style={{
            background: '#1f2230',
            border: '1px solid #383b48',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            color: '#fdfeff'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#567ad8' }}>Select Video for Comparison</h3>
            
            {/* Popup Blocked Warning */}
            {popupBlocked && (
              <div style={{
                background: '#ff6b6b',
                border: '1px solid #ff5252',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#fff'
              }}>
                <strong style={{ display: 'block', marginBottom: '0.5rem' }}>⚠️ Popup Blocked</strong>
                <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>
                  Your browser blocked the popup window. To use side-by-side comparison:
                </p>
                <ol style={{ margin: '0.5rem 0 0 1.2rem', padding: 0, fontSize: '0.85rem', lineHeight: '1.6' }}>
                  <li>Look for a popup icon in your browser's address bar</li>
                  <li>Click it and select "Always allow popups from this site"</li>
                  <li>Or go to Chrome Settings → Privacy → Site settings → Pop-ups</li>
                  <li>Add this site to the allowed list</li>
                  <li>Refresh the page and try again</li>
                </ol>
              </div>
            )}
            
            {/* File Upload Option */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Upload Video File:
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: '#11121b',
                  border: '1px solid #383b48',
                  borderRadius: '6px',
                  color: '#fdfeff'
                }}
              />
            </div>

            {/* Available Videos from Same Session */}
            {playerId && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Select from Previous Analyses:
                </label>
                {loadingVideos ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#a0a0a0' }}>Loading videos...</div>
                ) : availableVideos.length > 0 ? (
                  <select
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: '#11121b',
                      border: '1px solid #383b48',
                      borderRadius: '6px',
                      color: '#fdfeff',
                      marginBottom: '1rem'
                    }}
                    onChange={(e) => {
                      const selectedVideo = availableVideos.find(v => v.id === parseInt(e.target.value));
                      if (selectedVideo) {
                        // Store selected video - don't open window yet
                        setSelectedComparisonVideo({
                          url: selectedVideo.url,
                          name: selectedVideo.name,
                          isFile: false
                        });
                      } else {
                        setSelectedComparisonVideo(null);
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">-- Select a video --</option>
                    {availableVideos.map(video => (
                      <option key={video.id} value={video.id}>{video.name}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#a0a0a0', fontStyle: 'italic' }}>
                    No other videos available for comparison
                  </div>
                )}
              </div>
            )}

            {/* Selected Video Display */}
            {selectedComparisonVideo && (
              <div style={{
                background: '#2a2d3a',
                border: '1px solid #567ad8',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#fdfeff'
              }}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#567ad8' }}>
                  Selected for Comparison:
                </strong>
                <span style={{ fontSize: '0.9rem' }}>{selectedComparisonVideo.name}</span>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => {
                  // Clear selection when canceling
                  setSelectedComparisonVideo(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                  setShowComparisonModal(false);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#383b48',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fdfeff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStartComparison}
                disabled={!selectedComparisonVideo}
                style={{
                  padding: '0.5rem 1.5rem',
                  background: selectedComparisonVideo ? '#10b981' : '#555',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: selectedComparisonVideo ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  opacity: selectedComparisonVideo ? 1 : 0.5
                }}
                title={selectedComparisonVideo ? 'Open side-by-side comparison' : 'Please select a video first'}
              >
                Compare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugVideoCard;

